import { ref } from 'vue';
import { useRoute } from 'vue-router';
import { i18n } from '@/packages/i18n';

import { useIdentityQueueStore } from '@/stores/identityQueueStore';

import useLoggingEndpoint from '@/composables/api/useLoggingEndpoint';
import useModalActions from '@/composables/store/modal/useModalActions';
import useErrorHandling from '@/composables/api/useErrorHandling';
import useApiHelpers from '@/composables/api/helpers';
import useMocks from '@/composables/api/useMocks';

export default (token, emit, route, router) => {
  const SUPPORT_EMAIL = 'support@banxa.com';
  const PLAID_JS_URL = 'https://cdn.plaid.com/link/v2/stable/link-initialize.js';

  const identityQueueStore = useIdentityQueueStore();
  const { sleep } = useMocks();

  const { toggleActionModal } = useModalActions();
  const { postRequest } = useApiHelpers();
  const { logInfoOnServer } = useLoggingEndpoint();
  const { handleErrorInSpaFlow } = useErrorHandling();
  const { t } = i18n.global;

  const isPlaidLoaded = ref(false);
  const restrainToClick = ref(false);
  const userHasPassedSession = ref(false);
  let isCallingPlaidIDV;

  const terminateSessionEvent = 'IDENTITY_VERIFICATION_FAIL_SESSION';
  const successSessionEvent = 'IDENTITY_VERIFICATION_PASS_SESSION';
  const restartSessionEvent = 'IDENTITY_VERIFICATION_CLOSE_UI';
  let plaidLinkInstance;

  const showModalOnError = (errorTitle, errorMsg, primaryBtnText, primaryBtnAction) => {
    const modalOptions = {
      title: errorTitle,
      text: `${errorMsg}`,
      primaryBtnText,
      primaryBtnAction,
    };

    toggleActionModal(true, modalOptions);
  };

  const addPlaidScriptElement = () => {
    let plaidScript = document.getElementById('plaid-link-iframe-1');
    if (!plaidScript) {
      plaidScript = document.createElement('script');
      plaidScript.setAttribute('src', PLAID_JS_URL);
      document.head.appendChild(plaidScript);
    }
    return plaidScript;
  };

  const handleIdentityRedirect = () => {
    // eslint-disable-next-line no-use-before-define
    destroyPlaidLink();
    identityQueueStore.updateToNextRoute();
    identityQueueStore.fetchNextIdentityRoute(route).then(async (nextRoute) => {
      router.push(nextRoute);
    });
  };

  const reInitialisePlaidSession = async () => {
    // eslint-disable-next-line no-use-before-define
    await initialisePlaidLink();
    plaidLinkInstance.open();
  };

  /**
   * If the route has oauth_state_id query parameters,
   * then it means Plaid has routed the user back to the banxa ecosystem
  */
  const plaidReinitialiseRequired = () => !!route.query.oauth_state_id;

  const launchPlaidLink = () => {
    if (plaidLinkInstance) {
      plaidLinkInstance.open();
      // Restricting the user to click on the button again
      restrainToClick.value = true;
    }
  };

  const destroyPlaidLink = () => {
    plaidLinkInstance.exit({ force: true });
    plaidLinkInstance.destroy();
  };

  // Accessing plaidLinkToken from localStorage for handling the scenarios
  // when Plaid redirects the user back to Banxa ecosystem
  // Therefore, it should be removed onSuccess or onExit, so that new link token can be saved
  const createPlaidIframe = () => {
    const plaidObject = {
      token,
      onLoad() {
        isPlaidLoaded.value = true;
      },
      onSuccess(publicToken, metadata) {
        localStorage.removeItem('plaidLinkToken');
        emit('account-linked', publicToken, metadata);
      },
      onEvent: async (eventName, metadata) => {
        // skip for plaid payments
        if (!route?.name.includes('identity')) {
          return;
        }

        // plaid identity events
        if (eventName === successSessionEvent) {
          isCallingPlaidIDV = postRequest(
            '/documents/plaid',
            metadata,
            () => userHasPassedSession.value = true,
          );
        }

        if (eventName === terminateSessionEvent) {
          handleErrorInSpaFlow(
            227,
            t('PlaidId.error.finalRejection', { supportEmail: SUPPORT_EMAIL }),
          );
          return;
        }

        if (
          eventName === restartSessionEvent
        ) {
          if (userHasPassedSession.value || isCallingPlaidIDV) {
            await isCallingPlaidIDV;
            handleIdentityRedirect();
          } else {
            if (isCallingPlaidIDV) {
              await isCallingPlaidIDV;
              handleIdentityRedirect();
              return;
            }

            showModalOnError(
             t('ActionModal.title.plaidFailStep'),
             t('ActionModal.body.plaidFailStep'),
             t('ActionModal.primaryBtn.plaidFailStep'),
             async () => {
               destroyPlaidLink();
               await reInitialisePlaidSession();
             },
           );
          }
        }
      },
      onExit: (error, metadata) => {
        localStorage.removeItem('plaidLinkToken');
        // Allow the user to click on the button again if exited the flow with error
        restrainToClick.value = false;
        if (error) {
          logInfoOnServer({ error, metadata });
          emit('plaid-link-error', error, metadata);
        }

        // todo check what type of error is thrown here
        // throw modal only if session is closed by user
        if (route?.name.includes('identity')) {
          showModalOnError(
            t('ActionModal.title.plaidCloseSession'),
            t('ActionModal.body.plaidCloseSession'),
            t('ActionModal.primaryBtn.plaidCloseSession'),
            async () => {
              destroyPlaidLink();
              await reInitialisePlaidSession();
            },
          );
        }
      },
    };

    return plaidObject;
  };

  const initialisePlaidLink = async () => {
    const plaidObj = createPlaidIframe();
    // eslint-disable-next-line no-undef
    plaidLinkInstance = await Plaid.create(plaidObj);
  };

  const reInitialisePlaidLink = async () => {
    const plaidObj = createPlaidIframe();
      // eslint-disable-next-line no-undef
      plaidLinkInstance = await Plaid.create({
      ...plaidObj,
      receivedRedirectUri: window.location.href,
    });

    plaidLinkInstance.open();
  };

  return {
    addPlaidScriptElement,
    createPlaidIframe,
    initialisePlaidLink,
    reInitialisePlaidLink,
    plaidReinitialiseRequired,
    launchPlaidLink,
    restrainToClick,
    isPlaidLoaded,
    destroyPlaidLink,
  };
};
